import useUserTokenStore from "@/stores/user-token.store";
import { beginSignOut, endSignOut } from "@/lib/auth/authRecovery";
import { logger } from "@/lib/logger";
import useUserStore from "@/stores/user.store";
import { LogoutOptions } from "@auth0/auth0-react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import {
  requireAccessToken,
  type GetAccessTokenSilently,
} from "@/lib/auth/accessToken";

interface CustomJwtPayload extends JwtPayload {
  "https://markup.legal/organization_id"?: string;
}

/**
 * Async tenant id from live token; may throw if the silent call resolves without a token or the
 * JWT decode fails (preserves pre-cache call-site behavior).
 *
 * Throwing on a missing token matters here: returning `undefined` instead would read as "the
 * tenant changed" at every call site, which tears down and rebuilds the whole user context on
 * what is only a cache miss.
 */
export const getTenantIdFromToken = async (
  getAccessTokenSilently: GetAccessTokenSilently
) => {
  const token = requireAccessToken(
    await getAccessTokenSilently(),
    "getTenantIdFromToken"
  );
  const decodedToken = jwtDecode<CustomJwtPayload>(token);
  return decodedToken["https://markup.legal/organization_id"];
};

/** Decode organization (tenant) id from an access token string; null if missing or invalid. */
export const parseTenantIdFromAccessToken = (
  token: string | null | undefined
): string | null => {
  if (!token) return null;
  try {
    const decodedToken = jwtDecode<CustomJwtPayload>(token);
    const tenantId = decodedToken["https://markup.legal/organization_id"];
    return typeof tenantId === "string" ? tenantId : null;
  } catch {
    return null;
  }
};

/**
 * Clear tenant dialog session storage for a specific tenant
 * @param tenantId - The tenant ID to clear session storage for
 * @param sessionKeySuffix - Optional session key suffix (defaults to "guidelines-dialog-conversation")
 */
export const clearTenantDialogSessionStorage = (
  tenantId: string,
  sessionKeySuffix: string = "guidelines-dialog-conversation"
) => {
  try {
    if (!tenantId) return;

    const sessionKey = `tenant-${tenantId}-${sessionKeySuffix}-shown`;
    sessionStorage.removeItem(sessionKey);
  } catch (error) {
    logger.warn("Failed to clear tenant dialog session storage", {
      operation: "clearTenantDialogSessionStorage",
      error,
      tenantId,
    });
  }
};

export const handleLogout = (
  auth0logout: (options?: LogoutOptions) => Promise<void>
) => {
  try {
    const token = useUserTokenStore.getState().token;
    const tenantId = parseTenantIdFromAccessToken(token);
    if (tenantId) {
      clearTenantDialogSessionStorage(tenantId);
    }

    // Before the redirect: clearing the cache below leaves the app looking
    // authenticated with no token, and authRecovery would answer that with an
    // interactive login that beats the logout navigation (LAR-437).
    beginSignOut();
    auth0logout({ logoutParams: { returnTo: window.location.origin } });
    localStorage.clear();
    useUserTokenStore.setState({ token: "" });
  } catch (error) {
    endSignOut();
    logger.error("Failed to log out", {
      operation: "logout",
      error,
    });
  }
};

export const showOrganizationPicker = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginWithRedirect: (options?: any) => Promise<void>
) => {
  try {
    // Try using loginWithRedirect without prompt to see if it shows organization picker
    // If the user is already authenticated, this should show the organization picker
    loginWithRedirect({
      appState: {
        returnTo: `${window.location.origin}/auth?organization_switched=true`,
        organization_switched: true,
      },
      authorizationParams: {
        redirect_uri: `${window.location.origin}/auth`,
        // Remove prompt to see if Auth0 shows organization picker automatically
      },
    });
  } catch (error) {
    logger.error("Failed to show the organization picker", {
      operation: "showOrganizationPicker",
      error,
    });
  }
};

export const refreshUserContext = async (
  getAccessTokenSilently: GetAccessTokenSilently,
  onLogin: () => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateContext: (context: any) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  me: any,
  resetLoginFlag: boolean = true
) => {
  try {
    const token = requireAccessToken(
      await getAccessTokenSilently(),
      "refreshUserContext"
    );
    useUserTokenStore.getState().setToken(token);

    // Only reset the login flag if explicitly requested (e.g., on organization switch)
    if (resetLoginFlag) {
      useUserStore.getState().setHasCalledLogin(false);
    }

    const tenantId = parseTenantIdFromAccessToken(token);

    if (me) {
      useUserStore.setState({ me });
      updateContext({
        userId: me.id,
        properties: {
          tenantId,
        },
      });
      onLogin();
    }

    return tenantId;
  } catch (error) {
    logger.error("Failed to refresh user context", {
      operation: "refreshUserContext",
      error,
      resetLoginFlag,
    });
    throw error;
  }
};
